import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Lock, CheckCircle2, Circle, ChevronRight, GraduationCap, ArrowLeft } from "lucide-react";
import SEOHead from "../components/SEOHead";

import { useTranslation } from 'react-i18next';
export default function Course() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const [modules, setModules] = useState([]);
  const [completed, setCompleted] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [modRes, progRes] = await Promise.all([
          api.get("/course/modules"),
          api.get("/course/progress"),
        ]);
        setModules(modRes.data);
        setCompleted(new Set(progRes.data.completed));
      } catch (e) {
        if (e.response?.status === 403) setAccessError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={t('course.learning_center_max_employee')}
        description={t('course.max_employee_learning_center_courses_and')}
        url="/dashboard/course"
        noIndex
      />
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 font-mono text-sm text-zinc-500">{t('course.loading_course')}</div>
    </div>
  );

  if (accessError) return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <div className="w-16 h-16 mx-auto mb-8 bg-zinc-900 border border-zinc-800 flex items-center justify-center">
          <Lock className="w-8 h-8 text-amber-500" />
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// COURSE LOCKED</div>
        <h1 className="font-display text-4xl uppercase mb-4">{t('course.playbook_required')}</h1>
        <p className="text-zinc-400 leading-relaxed mb-8">
          The Playbook Online Course is available only to customers who have purchased the Playbook.
          Unlock 13 modules, 30+ lessons, and the full prompt library.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-sm transition-colors"
        >
          <GraduationCap className="w-4 h-4" /> Buy the Playbook
        </Link>
      </div>
    </div>
  );

  const totalLessons = modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
  const pct = totalLessons ? Math.round((completed.size / totalLessons) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />

      <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-0">
        {/* Sidebar */}
        <aside data-testid="course-sidebar" className="col-span-12 lg:col-span-4 xl:col-span-3 border-r border-zinc-900 min-h-[calc(100vh-4rem)]">
          <div className="sticky top-16 p-6">
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-zinc-500 hover:text-amber-500 font-mono text-[10px] uppercase tracking-[0.3em] mb-6">
              <ArrowLeft className="w-3 h-3" /> Dashboard
            </Link>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// PLAYBOOK COURSE</div>
            <h2 className="font-display text-2xl uppercase mb-6">{t('course.curriculum')}</h2>

            <div className="mb-8">
              <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                <span>{t('common.progress')}</span>
                <span>{pct}%</span>
              </div>
              <div className="h-1 bg-zinc-900 overflow-hidden">
                <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-2">
                {completed.size} / {totalLessons} LESSONS
              </div>
            </div>

            <nav className="space-y-5 overflow-y-auto max-h-[70vh] pr-2">
              {modules.map((m) => (
                <div key={m.module_id}>
                  <div className="font-display text-xs uppercase text-zinc-400 mb-3 border-b border-zinc-900 pb-2">
                    {m.title}
                  </div>
                  <ul className="space-y-1">
                    {(m.lessons || []).map((l) => {
                      const isDone = completed.has(l.lesson_id);
                      const active = l.lesson_id === lessonId;
                      return (
                        <li key={l.lesson_id}>
                          <Link
                            to={`/dashboard/course/${l.lesson_id}`}
                            data-testid={`course-lesson-${l.lesson_id}`}
                            className={`flex items-start gap-3 px-3 py-2 text-sm transition-colors ${
                              active
                                ? "bg-amber-500/10 border-l-2 border-amber-500 text-white"
                                : "border-l-2 border-transparent hover:border-zinc-700 hover:bg-zinc-950"
                            }`}
                          >
                            {isDone ? (
                              <CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            ) : (
                              <Circle className="w-4 h-4 text-zinc-700 mt-0.5 shrink-0" />
                            )}
                            <span className={active ? "text-white" : "text-zinc-400"}>{l.title}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </aside>

        {/* Lesson content */}
        <main className="col-span-12 lg:col-span-8 xl:col-span-9 min-h-[calc(100vh-4rem)]">
          {lessonId ? (
            <Outlet context={{ onComplete: () => setCompleted((prev) => new Set(prev).add(lessonId)), modules }} />
          ) : (
            <div className="p-12">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">// WELCOME</div>
              <h1 className="font-display text-5xl uppercase mb-6">The Playbook<br/>{t('course.course')}</h1>
              <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-10">
                13 modules. 30+ lessons. From zero to a personal AI agent that handles your computer
                work 24/7. Pick a lesson on the left to begin — your progress saves automatically.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 max-w-4xl">
                {[
                  { k: "13", l: "MODULES" },
                  { k: "30+", l: "LESSONS" },
                  { k: "∞", l: "PROMPTS" },
                ].map((s) => (
                  <div key={s.l} className="bg-[#09090B] p-8">
                    <div className="font-display text-5xl text-amber-500">{s.k}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-3">{s.l}</div>
                  </div>
                ))}
              </div>

              {modules[0]?.lessons?.[0] && (
                <Link
                  to={`/dashboard/course/${modules[0].lessons[0].lesson_id}`}
                  data-testid="start-course-button"
                  className="mt-10 inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors"
                >
                  Start with Lesson 1 <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
